import React, { useMemo, useRef, useState, useEffect } from 'react';

function Image({
  src,
  alt = "Image Name",
  className = "",
  ...props
}) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const fallbackIndexRef = useRef(0);

  const fallbackCandidates = useMemo(() => {
    const normalizedAlt = String(alt || '')
      .toLowerCase()
      .replace(/logo/g, '')
      .trim();

    const baseSlug = normalizedAlt
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    const variantsSet = new Set();
    if (baseSlug) {
      variantsSet.add(baseSlug);
      variantsSet.add(baseSlug.replace(/-app$/, ''));
      variantsSet.add(baseSlug.replace(/-inc$/, ''));
      variantsSet.add(baseSlug.replace(/-corporation$|-corp$/, ''));
      variantsSet.add(baseSlug.replace(/-company$/, ''));
      variantsSet.add(baseSlug.replace(/-/g, ''));
      const firstToken = baseSlug.split('-')[0];
      if (firstToken) variantsSet.add(firstToken);
    }

    const variants = Array.from(variantsSet).filter(Boolean);

    const paths = [];
    variants.forEach((v) => {
      paths.push(`/assets/images/${v}.png`);
      paths.push(`/assets/images/${v}.svg`);
      paths.push(`/assets/images/${v}-logo.png`);
      paths.push(`/assets/images/${v}-logo.svg`);
    });

    // Always end with generic fallback
    paths.push('/assets/images/no_image.png');
    return paths;
  }, [alt]);

  // Keep image in sync when the src prop changes (e.g., carousels)
  useEffect(() => {
    fallbackIndexRef.current = 0;
    setCurrentSrc(src);
  }, [src]);

  const handleError = () => {
    const nextIndex = fallbackIndexRef.current + 1;
    fallbackIndexRef.current = nextIndex;
    const nextSrc = fallbackCandidates[nextIndex] || '/assets/images/no_image.png';
    setCurrentSrc(nextSrc);
  };

  return (
    <img
      src={currentSrc}
      alt={alt}
      className={className}
      onError={handleError}
      loading="lazy"
      decoding="async"
      {...props}
    />
  );
}

export default Image;
